import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/faro";
import {
  PRECIO_PUNTOS as DEFAULT_PRECIO_PUNTOS,
  PRECIO_CARNET as DEFAULT_PRECIO_CARNET,
  formatEuro,
  formatEuros,
} from "@/lib/courses";

// Cache de módulo para no re-fetchear en cada mount durante la misma sesión.
let cache = null;
let inflight = null;

/**
 * Precios de los cursos (puntos / carnet) leídos en tiempo real desde
 * `/api/config`. Si aún no ha llegado la respuesta o algo falla se sirven
 * los defaults de `lib/courses.js` (215 / 405), por lo que la página nunca
 * se queda sin precio visible.
 *
 * Retorna:
 *   {
 *     puntos, carnet,                     // números (Ej. 215, 405)
 *     puntosEur, carnetEur,               // "215 €", "405 €"
 *     puntosEuros, carnetEuros,           // "215 euros", "405 euros"
 *     puntosPrice, carnetPrice,           // "215", "405" (Schema.org)
 *     loaded,                             // boolean, true si llegó del backend
 *   }
 */
export function usePricing() {
  const [prices, setPrices] = useState(() => cache || { puntos: DEFAULT_PRECIO_PUNTOS, carnet: DEFAULT_PRECIO_CARNET });
  const [loaded, setLoaded] = useState(!!cache);

  useEffect(() => {
    if (cache) return;
    if (!inflight) {
      inflight = axios.get(`${API}/config`)
        .then((r) => {
          const p = r?.data?.course_type_prices || {};
          const out = {
            puntos: Number.isFinite(+p.puntos) && +p.puntos > 0 ? +p.puntos : DEFAULT_PRECIO_PUNTOS,
            carnet: Number.isFinite(+p.carnet) && +p.carnet > 0 ? +p.carnet : DEFAULT_PRECIO_CARNET,
          };
          cache = out;
          return out;
        })
        .catch(() => ({ puntos: DEFAULT_PRECIO_PUNTOS, carnet: DEFAULT_PRECIO_CARNET }));
    }
    let alive = true;
    inflight.then((out) => {
      if (!alive) return;
      setPrices(out);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  return {
    puntos: prices.puntos,
    carnet: prices.carnet,
    puntosEur: formatEuro(prices.puntos),
    carnetEur: formatEuro(prices.carnet),
    puntosEuros: formatEuros(prices.puntos),
    carnetEuros: formatEuros(prices.carnet),
    puntosPrice: String(prices.puntos),
    carnetPrice: String(prices.carnet),
    loaded,
  };
}

export default usePricing;
